import { useLocale, useNow } from "@i18n/react";
import { differenceInDays } from "date-fns";
import { differenceInHours } from "date-fns/differenceInHours";
import { differenceInMinutes } from "date-fns/differenceInMinutes";
import { differenceInSeconds } from "date-fns/differenceInSeconds";
import { useEffect, useState } from "react";

export type UseCountdownProps = {
  targetDate: string;
  format?: "days" | "hours" | "minutes" | "seconds" | "default";
};

export const useCountdown = ({
  targetDate,
  format = "default",
}: UseCountdownProps) => {
  const now = useNow({ updateInterval: 1000 });
  const locale = useLocale();
  const [mounted, setMounted] = useState<boolean>(false);

  useEffect(() => {
    setMounted(typeof window !== "undefined");
  }, []);

  const formatCountdown = () => {
    // time-based formatting can cause hydration errors, so wait to render this until the client
    if (mounted) {
      const target = new Date(targetDate);
      const difference = differenceInSeconds(target, new Date(now));

      if (difference > 0) {
        const days = differenceInDays(target, now);
        const hours = differenceInHours(target, now);
        const minutes = differenceInMinutes(target, now);
        const seconds = difference % 60;

        switch (format) {
          case "days":
            return days;
          case "hours":
            return hours;
          case "minutes":
            return minutes;
          case "seconds":
            return seconds;
        }

        if (days > 0) {
          return Intl.NumberFormat(locale, {
            style: "unit",
            unit: "day",
            unitDisplay: "long",
          }).format(days);
        } else if (hours > 0) {
          return Intl.NumberFormat(locale, {
            style: "unit",
            unit: "hour",
            unitDisplay: "long",
          }).format(hours);
        } else {
          return `${String(minutes).padStart(2, "0")}:${String(
            seconds,
          ).padStart(2, "0")}`;
        }
      }
    }
    return "";
  };

  return formatCountdown();
};
